import { Button, Card, LinearProgress, Typography } from '@mui/material';
import { format } from 'date-fns';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Colors } from 'src/components/utils/Theme';
import { IForumOptions, IForumVotings } from 'src/models';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

interface Props {
  voting: IForumVotings;
  startVotingCallback: () => void;
}

export const ForumVotingDetail: FC<Props> = ({ voting, startVotingCallback }) => {
  const { t } = useTranslation();

  const getFormattedDate = (date: Date) => {
    return format(date, 'd. MMMM yyyy HH:mm');
  };

  const getVotingDate = () => {
    if (voting.status === 'ongoing') {
      const date = new Date(voting.timestamps.ongoing ?? '');
      date.setDate(date.getDate());
      return date;
    } else if (voting.status === 'upcoming') {
      const date = new Date(voting.timestamps.upcoming ?? '');
      date.setDate(date.getDate() + 2);
      return date;
    } else {
      return new Date();
    }
  };

  const today = new Date();
  return (
    <Card sx={{ p: 2 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 6 }}>
        <Typography variant="h6">{voting.title}</Typography>
        <Typography variant="subtitle1">
          {getFormattedDate(new Date(voting.createdAt))} ({voting.userName})
        </Typography>
      </div>

      <div>
        {voting.status === 'upcoming' && today < getVotingDate() && (
          <div>
            {t('dashboard.assets.forum.voting.status.upcoming_voting')} - {t('dashboard.assets.forum.voting.status.open_until')}:{' '}
            {getVotingDate().toLocaleDateString('de-DE')},{' '}
            {getVotingDate()
              .toLocaleTimeString('de-DE')
              .replace(/(.*)\D\d+/, '$1')}
          </div>
        )}
        {voting.status === 'upcoming' && today > getVotingDate() && (
          <div>
            {t('dashboard.assets.forum.voting.status.upcoming_voting')} - {t('dashboard.assets.forum.voting.status.completed_icon')}
          </div>
        )}

        {/* <!-- ===== SUGGESTED VOTING ===== --> */}
        {voting.status === 'suggestion' &&
          (voting?.suggestedVoters?.shares ?? 0) > 10 &&
          (voting?.suggestedVoters?.shareholder ?? 0) > 10 && (
            <div v-if="">
              {t('dashboard.assets.forum.voting.status.proposal_for_voting')} -{' '}
              {t('dashboard.assets.forum.voting.status.approval_received')}
            </div>
          )}

        {voting.status === 'suggestion' &&
          (voting?.suggestedVoters?.shares ?? 0) < 10 &&
          (voting?.suggestedVoters?.shareholder ?? 0) < 10 && (
            <div>
              {t('dashboard.assets.forum.voting.status.proposal_for_voting')} - {t('dashboard.assets.forum.voting.status.open_until')}
            </div>
          )}
        {voting.status === 'suggestionFailed' && (
          <div>
            {t('dashboard.assets.forum.voting.status.proposal_for_voting')} -{' '}
            {t('dashboard.assets.forum.voting.status.no_consent_received')}
          </div>
        )}

        {/* <!-- ===== RUNNING VOTING ===== --> */}
        {voting.status === 'ongoing' && (
          <div>
            {t('dashboard.assets.forum.voting.status.ongoing_voting')} - {t('dashboard.assets.forum.voting.status.open_until')}:{' '}
            {getVotingDate().toLocaleDateString('de-DE')},{' '}
            {getVotingDate()
              .toLocaleTimeString('de-DE')
              .replace(/(.*)\D\d+/, '$1')}
          </div>
        )}

        {/* <!-- ===== Closed VOTING ===== --> */}
        {voting.status === 'done' && (
          <div>
            {t('dashboard.assets.forum.voting.status.completed_icon')} - {t('dashboard.assets.forum.voting.status.approval_received')}
          </div>
        )}
      </div>

      <Typography fontWeight="bold" variant="body1" style={{ paddingTop: 12, paddingBottom: 24, whiteSpace: 'pre-wrap' }}>
        {voting.text}
      </Typography>

      <div>
        {(voting.status === 'suggestion' || voting.status === 'suggestionFailed') && (
          <LinearProgress value={voting.suggestedVoters?.shares ?? 0} />
        )}
        {voting.options?.map((option, index) => (
          <VotingDetailOption key={index} voting={voting} votingOption={option} />
        ))}
      </div>
      {voting.status === 'suggestion' && (voting.suggestedVoters?.shares ?? 0) >= 10 && (
        <Button onClick={startVotingCallback}>{t('dashboard.assets.forum.voting.start')}</Button>
      )}
    </Card>
  );
};

interface VotingDetailOptionProps {
  votingOption: IForumOptions;
  voting: IForumVotings;
}
export const VotingDetailOption: FC<VotingDetailOptionProps> = ({ voting, votingOption }) => {
  return (
    <Card sx={{ p: 2, mb: 2, backgroundColor: Colors.GREY50 }}>
      <Typography fontWeight="bold" fontSize={16}>
        {votingOption.title}
      </Typography>
      <Typography variant="subtitle2" paddingBottom={2}>
        {votingOption.text}
      </Typography>
      <Typography variant="subtitle2" paddingBottom={2}>
        {/* --> upcoming --> suggestedVoters */}
        {/* --> anders --> voters */}
        {voting.status === 'upcoming' ? voting.suggestedVoters?.shares ?? 0 : votingOption.voters?.shares ?? 0} %
      </Typography>
      {votingOption.attachments?.map((attachment) => {
        return (
          <Card
            onClick={() => {
              window.open(attachment.url, '_blank');
            }}
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: Colors.GREY50,
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>{attachment.name}</div>
            <FileDownloadOutlinedIcon />
          </Card>
        );
      })}
    </Card>
  );
};
